import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {Notification} from '../shared/components/notification/notification.component';

export interface NotificationConfig {
  title?: string;
  duration?: number;
  action?: {
    label: string;
    handler: (onClose: () => void) => void;
  };
  dismissible?: boolean;
  hideIcon?: boolean;
  /**
   * Correlation key for a background task (MAP-1579). Use the same key for the
   * progress notification (`info`/`loading`) and for the completion
   * notification (`success`/`error`/`warning`) of one task, typically the job
   * id. When the completion is raised, a progress notification still on screen
   * for that key is torn down, so the user never sees "in progress" next to
   * "done". Correlation is by key only, never by message text (i18n-safe).
   */
  taskKey?: string;
}

export interface NotificationRef {
  id: string;
  dismiss: () => void;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notifications: Notification[] = [];
  private notificationsSubject = new BehaviorSubject<Notification[]>([]);
  // MAP-1579: taskKey -> id of the progress notification currently shown for
  // that background task.
  private progressByTask = new Map<string, string>();

  public notifications$ = this.notificationsSubject.asObservable();

  private generateId(): string {
    return `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Register a progress notification so the completion notification of the same
   * task can supersede it. Re-registering the notification that is already the
   * progress of that task is a no-op, so it is never torn down and recreated.
   * A *different* progress notification for the same task replaces the first.
   */
  private trackProgress(notification: Notification): void {
    if (!notification.taskKey) return;
    if (this.progressByTask.get(notification.taskKey) === notification.id) return;
    this.dismissProgressForTask(notification.taskKey);
    this.progressByTask.set(notification.taskKey, notification.id);
  }

  /**
   * Tear down the progress notification of a task, if one is still displayed.
   * Safe to call for unknown/already-dismissed tasks: it is a no-op then.
   */
  private dismissProgressForTask(taskKey?: string): void {
    if (!taskKey) return;
    const id = this.progressByTask.get(taskKey);
    if (!id) return;
    this.progressByTask.delete(taskKey);
    this.dismiss(id);
  }

  private addNotification(notification: Notification): NotificationRef {
    this.notifications = [...this.notifications, notification];
    this.notificationsSubject.next(this.notifications);

    const ref: NotificationRef = {
      id: notification.id,
      dismiss: () => this.dismiss(notification.id)
    };

    return ref;
  }

  /**
   * Show a success notification
   */
  success(message: string, config?: NotificationConfig): NotificationRef {
    const notification: Notification = {
      id: this.generateId(),
      type: 'success',
      title: config?.title,
      message,
      duration: config?.duration || 0,
      action: config?.action,
      dismissible: config?.dismissible ?? true,
      hideIcon: config?.hideIcon ?? false,
      taskKey: config?.taskKey
    };

    this.dismissProgressForTask(config?.taskKey);
    return this.addNotification(notification);
  }

  /**
   * Show an error notification
   */
  error(message: string, config?: NotificationConfig): NotificationRef {
    const notification: Notification = {
      id: this.generateId(),
      type: 'error',
      title: config?.title,
      message,
      duration: config?.duration ?? 8000, // Longer duration for errors
      action: config?.action,
      dismissible: config?.dismissible ?? true,
      hideIcon: config?.hideIcon ?? false,
      taskKey: config?.taskKey
    };

    this.dismissProgressForTask(config?.taskKey);
    return this.addNotification(notification);
  }

  /**
   * Show a warning notification
   */
  warning(message: string, config?: NotificationConfig): NotificationRef {
    const notification: Notification = {
      id: this.generateId(),
      type: 'warning',
      title: config?.title,
      message,
      duration: config?.duration ?? 6000,
      action: config?.action,
      dismissible: config?.dismissible ?? true,
      hideIcon: config?.hideIcon ?? false,
      taskKey: config?.taskKey
    };

    this.dismissProgressForTask(config?.taskKey);
    return this.addNotification(notification);
  }

  /**
   * Show an info notification
   */
  info(message: string, config?: NotificationConfig): NotificationRef {
    const notification: Notification = {
      id: this.generateId(),
      type: 'info',
      title: config?.title,
      message,
      duration: config?.duration ?? 5000,
      action: config?.action,
      dismissible: config?.dismissible ?? true,
      hideIcon: config?.hideIcon ?? false,
      taskKey: config?.taskKey
    };

    const ref = this.addNotification(notification);
    // Blue info is how background tasks announce "in progress"; with a taskKey
    // it becomes the progress notification the completion will supersede.
    this.trackProgress(notification);
    return ref;
  }

  /**
   * Show a loading notification
   */
  loading(message: string, config?: NotificationConfig): NotificationRef {
    const notification: Notification = {
      id: this.generateId(),
      type: 'loading',
      title: config?.title,
      message,
      duration: 0, // Don't auto-dismiss loading notifications
      action: config?.action,
      dismissible: config?.dismissible ?? false,
      hideIcon: config?.hideIcon ?? false,
      taskKey: config?.taskKey
    };

    const ref = this.addNotification(notification);
    this.trackProgress(notification);
    return ref;
  }

  /**
   * Re-target a notification that is already on screen, keeping its id. The
   * container tracks by id, so the card morphs in place instead of being torn
   * down and recreated: a background task that first reports "uploading" and
   * then "in progress" shows one continuous card rather than two look-alike
   * cards flashing in succession. No-op once the notification is gone.
   *
   * Passing a `taskKey` here registers the card as that task's progress
   * notification, so the completion notification still supersedes it.
   */
  update(id: string, changes: Partial<Omit<Notification, 'id'>>): void {
    const index = this.notifications.findIndex(n => n.id === id);
    if (index === -1) return;

    const previous = this.notifications[index];
    const updated: Notification = {...previous, ...changes, id};
    this.notifications = [
      ...this.notifications.slice(0, index),
      updated,
      ...this.notifications.slice(index + 1)
    ];

    if (previous.taskKey && previous.taskKey !== updated.taskKey && this.progressByTask.get(previous.taskKey) === id) {
      this.progressByTask.delete(previous.taskKey);
    }
    this.notificationsSubject.next(this.notifications);

    if (updated.type === 'info' || updated.type === 'loading') {
      this.trackProgress(updated);
    }
  }

  /**
   * Dismiss a specific notification. Idempotent: dismissing an id that is no
   * longer displayed (auto-expired, already superseded) does nothing.
   */
  dismiss(id: string): void {
    const dismissed = this.notifications.find(n => n.id === id);
    if (dismissed?.taskKey && this.progressByTask.get(dismissed.taskKey) === id) {
      this.progressByTask.delete(dismissed.taskKey);
    }
    this.notifications = this.notifications.filter(n => n.id !== id);
    this.notificationsSubject.next(this.notifications);
  }

  /**
   * Close all active notifications
   */
  closeAll(): void {
    this.notifications = [];
    this.progressByTask.clear();
    this.notificationsSubject.next(this.notifications);
  }

  /**
   * Get the count of active notifications
   */
  getActiveCount(): number {
    return this.notifications.length;
  }

  /**
   * Get all active notifications
   */
  getAll(): Notification[] {
    return [...this.notifications];
  }
}
